/* DRIVE IQ — ADVENTURE MODE V601.2 SHOPPING CANDIDATE AUTHORITY
   File: adventure-candidate-source.js

   V601.2 focused repair:
   - Adds Shopping as a real Geoapify candidate category instead of silently
     falling back to Local Favorites.
   - Uses supported commercial.shopping_mall, department_store, discount_store,
     clothing, marketplace, antiques, art, books, jewelry, second_hand, and
     outdoor-and-sport category families.
   - Gives Shopping its own target, pass order, radius expansion, and recovery floor.
   - Preserves every existing destination, timeout, pagination, continuation,
     deduplication, recovery, Voice IQ, UI, pin, shelf, and ranking authority.

   V601.0 focused repair:
- Connects Candidate IQ directly to Destination IQ expected inventory and recovery decisions.
- Uses Destination IQ search anchors for large, complex, district, island, park, and water destinations.
- Runs a deterministic recovery sequence when the first provider pass is implausibly sparse.
- Merges and deduplicates all successful anchor and radius recovery results before returning.
- Prevents a normal city from exposing a false zero-result scan after only one weak center pass.
- Preserves V600.5 safe per-pass timeout behavior and all existing category pass plans.

   V600.5 focused repair:
   - Preserves every V600.4 category pass, pass order, radius plan, pagination rule,
     continuation behavior, category family, and provider limit alias.
   - Adds a bounded timeout to each individual Geoapify request so one slow pass
     cannot hold the entire Adventure scan open.
   - Reliably clears timeout timers on success, HTTP failure, JSON failure, abort,
     and provider exceptions.
   - Treats one timed-out pass as an empty optional pass, preserves candidates
     already collected from successful passes, and continues the existing plan.
   - Keeps continuation rounds force-running every useful fresh provider pass.
   - Adds market-aware raw candidate targets without changing shelf behavior:
     stronger depth for tourist and major markets, and a modest recall floor for
     sparse Food IQ and Live Music scans.
   - Does not modify adventure-scan.js V600.70, shelf quantity, pins, Place IQ,
     or the three-round discovery model.

   V600.4 focused repair:
   - Makes discovery rounds request genuinely different Geoapify result pages.
   - Uses provider-supplied discoveryRound, continuationMode, categoryCacheKey,
     requestedCategory, bypassCache, and noCache fields.
   - Adds round-aware offset pagination and removes proximity bias on later rounds.
   - Expands radius progressively for rounds two and three.
   - Runs every useful provider pass during continuation rounds.
   - Gives Trending and Worth the Drive distinct category plans instead of
     silently reusing the Local Favorites pool.
   - Always resolves continuation searches, including honest zero-result rounds.
   - Emits page, offset, round, cache key, and fresh-result diagnostics.

   Fixes:
   - Uses current Geoapify Places category paths for coffee discovery.
   - Honors canonicalQuery, forceFreshMarket, resetDiscovery, requestId, and exact center.
   - Runs targeted category passes and merges unique candidates.
   - Expands sparse Near Me searches through controlled radius fallback.
   - Prevents one narrow category response from producing a false zero-result scan.
   - Preserves raw Geoapify properties for Category Truth, identity, and photo enrichment.
   - Emits candidate-source diagnostics for each provider pass.
   - Fixes canonical aliases such as water, dessert, family, local favorites, and scenic.
   - Gives each category its own provider passes, radius tolerance, and candidate target.
   - Prevents Water from silently falling back to Local Favorites.
   - Continues safely when an optional provider pass fails.
   - Honors maximumResults, maxResults, limit, pageSize, candidateLimit, and resultLimit.
   - Uses Scan IQ marketVolumeProfile and targetResults instead of category defaults alone.
   - Prevents tourist and major markets from stopping after a small first-pass result set.
   - Runs all useful category passes until the market-aware target is genuinely satisfied.
   - Emits requested, effective target, provider limit, and shortfall diagnostics.
*/
(function(window){
"use strict";

if(window.DriveIQAdventureCandidateSource){return;}

var cfg=window.DriveIQAdventureConfig;
var events=window.DriveIQAdventureEvents||null;
var destinationAuthority=window.DriveIQAdventureDestination||null;
var requestHistory={};
var MAX_REQUEST_HISTORY=80;
var GEOAPIFY_PASS_TIMEOUT_MS=4500;
var GEOAPIFY_PASS_TIMEOUT_MIN_MS=1800;

if(!cfg){
  throw new Error("Candidate Source requires adventure-config.js.");
}

var categoryMap={
  food:["catering.restaurant","catering.cafe","catering.fast_food","catering.food_court"],
  coffee:["catering.cafe.coffee_shop","catering.cafe.coffee","catering.cafe"],
  dessert:["catering.cafe.ice_cream","catering.cafe","catering"],
  local_favorites:["catering","entertainment","leisure","tourism.attraction"],
  trending:["catering","entertainment","tourism.attraction"],
  live_music:["entertainment.music_venue","catering.bar","catering.pub"],
  hidden_gems:["tourism.attraction","leisure","catering.restaurant","catering.cafe"],
  waterfront:["natural.water","beach","leisure.park","tourism.attraction"],
  outdoors:["leisure.park","natural","tourism.attraction","camping"],
  lake_iq:["natural.water","beach","camping","leisure.park","tourism.attraction"],
  sunset_iq:["natural","beach","leisure.park","tourism.attraction"],
  family_iq:["entertainment","leisure","tourism.attraction","leisure.park","catering.cafe.ice_cream"],
  dog_iq:["leisure.park","natural","catering.restaurant"],
  gameday:["catering.bar","catering.pub","entertainment","catering.restaurant"],
  nightlife:["catering.bar","catering.pub","entertainment"],
  shopping:[
    "commercial.shopping_mall",
    "commercial.department_store",
    "commercial.discount_store",
    "commercial.clothing",
    "commercial.marketplace",
    "commercial.antiques",
    "commercial.art",
    "commercial.books",
    "commercial.jewelry",
    "commercial.second_hand",
    "commercial.outdoor_and_sport"
  ]
};

function clean(value){
  return String(value==null?"":value)
    .replace(/\s+/g," ")
    .trim();
}

function norm(value){
  return clean(value).toLowerCase();
}

function firstDefined(){
  var i;

  for(i=0;i<arguments.length;i+=1){
    if(
      arguments[i]!==undefined&&
      arguments[i]!==null&&
      arguments[i]!==""
    ){
      return arguments[i];
    }
  }

  return undefined;
}

function categoryKey(value){
  return norm(value)
    .replace(/[^a-z0-9]+/g,"_")
    .replace(/^_+|_+$/g,"");
}

function canonicalCategory(value){
  var key=categoryKey(value);
  var aliases={
    coffee_iq:"coffee",coffees:"coffee",cafes:"coffee",cafe:"coffee",
    restaurants:"food",restaurant:"food",food_iq:"food",dining:"food",
    desserts:"dessert",treats:"dessert",sweet_treats:"dessert",ice_cream:"dessert",
    local_favorite:"local_favorites",local_faves:"local_favorites",favorites:"local_favorites",
    worth_the_drive:"worth_the_drive",worth_drive:"worth_the_drive",worth_it:"worth_the_drive",
    music:"live_music",live:"live_music",live_music_iq:"live_music",
    hidden_gem:"hidden_gems",gems:"hidden_gems",
    water:"waterfront",water_iq:"waterfront",waterfront_iq:"waterfront",
    beaches:"waterfront",beach_access:"waterfront",
    outdoor:"outdoors",outdoor_iq:"outdoors",scenic:"outdoors",scenic_iq:"outdoors",parks:"outdoors",
    family:"family_iq",family_fun:"family_iq",kids:"family_iq",
    nightlife_iq:"nightlife",bars:"nightlife",
    shop:"shopping",shops:"shopping",store:"shopping",stores:"shopping",
    retail:"shopping",retail_shopping:"shopping",shopping_iq:"shopping",
    mall:"shopping",malls:"shopping",shopping_center:"shopping",
    shopping_centers:"shopping",outlet:"shopping",outlets:"shopping",
    boutique:"shopping",boutiques:"shopping"
  };
  return aliases[key]||key||"local_favorites";
}

function discoveryRound(context){
  return Math.max(
    1,
    Number(
      context&&(
        context.discoveryRound||
        context.continuationRound||
        context.round||
        1
      )
    )
  );
}

function continuationMode(context){
  return categoryKey(
    context&&(
      context.continuationMode||
      context.refillMode||
      ""
    )
  );
}

function requestSessionKey(context,center,category){
  return String(
    context&&(
      context.categoryCacheKey||
      context.cacheKey
    )||
    [
      canonicalCategory(category),
      Number(center&&center.lat||0).toFixed(4),
      Number(center&&center.lng||0).toFixed(4),
      String(context&&context.scope||"")
    ].join("|")
  );
}

function trimRequestHistory(){
  var keys=Object.keys(requestHistory);

  if(keys.length<=MAX_REQUEST_HISTORY){
    return;
  }

  keys.sort(function(a,b){
    return Number(requestHistory[a]&&requestHistory[a].updatedAt||0)-
      Number(requestHistory[b]&&requestHistory[b].updatedAt||0);
  });

  while(keys.length>MAX_REQUEST_HISTORY){
    delete requestHistory[keys.shift()];
  }
}

function categories(category){
  var key=canonicalCategory(category);
  return (categoryMap[key]||categoryMap.local_favorites).slice();
}

function finiteNumber(value){
  var number=Number(value);
  return isFinite(number)?number:null;
}

function candidateKey(item){
  return String(
    item.placeId||
    item.place_id||
    item.id||
    [
      norm(item.name||item.label),
      Number(item.lat).toFixed(5),
      Number(item.lng).toFixed(5)
    ].join("|")
  );
}

function normalizeFeature(feature,query){
  var p=feature&&feature.properties
    ? feature.properties
    : {};
  var coords=
    feature&&
    feature.geometry&&
    feature.geometry.coordinates
      ? feature.geometry.coordinates
      : [];
  var lat;
  var lng;
  var name;

  if(coords.length<2){return null;}

  lat=finiteNumber(coords[1]);
  lng=finiteNumber(coords[0]);

  if(lat===null||lng===null){return null;}

  name=clean(
    p.name||
    p.address_line1||
    p.formatted||
    ""
  );

  if(!name){return null;}

  return {
    placeId:String(
      p.place_id||
      (
        p.datasource&&
        p.datasource.raw&&
        (
          p.datasource.raw.place_id||
          p.datasource.raw.osm_id
        )
      )||
      ""
    ),
    id:String(p.place_id||""),
    label:name,
    name:name,
    full:clean(p.formatted||p.address_line2||""),
    address:clean(p.formatted||p.address_line2||""),
    city:clean(p.city||p.town||p.village||p.county||""),
    state:clean(p.state||""),
    postcode:clean(p.postcode||""),
    country:clean(p.country||""),
    category:String(
      (p.categories&&p.categories[0])||
      p.category||
      ""
    ),
    primaryCategory:String(
      (p.categories&&p.categories[0])||
      p.category||
      ""
    ),
    categories:Array.isArray(p.categories)
      ? p.categories.slice()
      : [],
    lat:lat,
    lng:lng,
    distanceMeters:Number(p.distance||0),
    openNow:
      p.opening_hours&&
      typeof p.opening_hours.open_now==="boolean"
        ? p.opening_hours.open_now
        : undefined,
    datasource:p.datasource||null,
    properties:p,
    raw:feature,
    source:"geoapify-v600.3",
    sourceQuery:String(query||"")
  };
}

function emit(name,payload){
  try{
    if(events&&typeof events.emit==="function"){
      events.emit(name,payload||{});
    }
  }catch(error){}
}

function buildURL(options){
  var key=String(
    cfg.api&&cfg.api.geoapifyKey||""
  );
  var center=options.center;
  var categoriesValue=options.categories.join(",");
  var params=[
    "categories="+encodeURIComponent(categoriesValue),
    "filter=circle:"+encodeURIComponent(
      String(center.lng)+","+
      String(center.lat)+","+
      String(options.radiusMeters)
    ),
    "limit="+encodeURIComponent(
      String(options.limit)
    ),
    "offset="+encodeURIComponent(
      String(Math.max(0,Number(options.offset||0)))
    ),
    "lang=en",
    "apiKey="+encodeURIComponent(key)
  ];

  if(options.biasProximity!==false){
    params.splice(
      2,
      0,
      "bias=proximity:"+encodeURIComponent(
        String(center.lng)+","+
        String(center.lat)
      )
    );
  }

  return "https://api.geoapify.com/v2/places?"+
    params.join("&");
}

function fetchPass(options){
  var url=buildURL(options);
  var controller=
    typeof window.AbortController==="function"
      ? new window.AbortController()
      : null;
  var timeoutMs=Math.max(
    GEOAPIFY_PASS_TIMEOUT_MIN_MS,
    Number(options.timeoutMs||GEOAPIFY_PASS_TIMEOUT_MS)
  );
  var timeoutId=null;
  var settled=false;

  function clearPassTimeout(){
    if(timeoutId){
      window.clearTimeout(timeoutId);
      timeoutId=null;
    }
  }

  function settle(){
    if(settled){return false;}
    settled=true;
    clearPassTimeout();
    return true;
  }

  emit("candidate-source:requested",{
    requestId:options.requestId,
    category:options.category,
    categories:options.categories.slice(),
    radiusMeters:options.radiusMeters,
    center:options.center,
    pass:options.pass,
    query:options.query,
    limit:options.limit,
    offset:options.offset||0,
    discoveryRound:options.discoveryRound||1,
    biasProximity:options.biasProximity!==false,
    sessionKey:options.sessionKey||"",
    effectiveTarget:
      options.effectiveTarget||null,
    timeoutMs:timeoutMs
  });

  if(controller){
    timeoutId=window.setTimeout(function(){
      if(settled){return;}
      try{
        controller.abort();
      }catch(error){}
    },timeoutMs);
  }

  return fetch(url,{
    method:"GET",
    cache:options.bypassCache
      ? "no-store"
      : "default",
    signal:controller?controller.signal:undefined
  })
    .then(function(response){
      if(!response.ok){
        return response.text()
          .catch(function(){return "";})
          .then(function(body){
            throw new Error(
              "Geoapify places returned "+
              response.status+
              (body?" — "+body.slice(0,180):"")
            );
          });
      }

      return response.json();
    })
    .then(function(data){
      var output=[];

      settle();

      (data&&data.features||[]).forEach(function(feature){
        var item=normalizeFeature(
          feature,
          options.query
        );

        if(item){
          output.push(item);
        }
      });

      emit("candidate-source:pass-completed",{
        requestId:options.requestId,
        pass:options.pass,
        category:options.category,
        categories:options.categories.slice(),
        radiusMeters:options.radiusMeters,
        resultCount:output.length,
        offset:options.offset||0,
        discoveryRound:options.discoveryRound||1,
        biasProximity:options.biasProximity!==false,
        sessionKey:options.sessionKey||"",
        timeoutMs:timeoutMs
      });

      return output;
    })
    .catch(function(error){
      var isTimeout=!!(
        error&&
        (
          error.name==="AbortError"||
          /abort|timeout/i.test(
            String(error.message||error)
          )
        )
      );

      settle();

      if(isTimeout){
        emit("candidate-source:pass-timeout",{
          requestId:options.requestId,
          pass:options.pass,
          category:options.category,
          categories:options.categories.slice(),
          radiusMeters:options.radiusMeters,
          offset:options.offset||0,
          discoveryRound:options.discoveryRound||1,
          sessionKey:options.sessionKey||"",
          timeoutMs:timeoutMs
        });

        return [];
      }

      throw error;
    });
}

function mergeCandidates(groups){
  var output=[];
  var seen={};

  (groups||[]).forEach(function(group){
    (group||[]).forEach(function(item){
      var key=candidateKey(item);

      if(!key||seen[key]){return;}

      seen[key]=true;
      output.push(item);
    });
  });

  output.sort(function(a,b){
    return Number(a.distanceMeters||0)-
      Number(b.distanceMeters||0);
  });

  return output;
}

function categoryProfile(category){
  var key=canonicalCategory(category);
  var profiles={
    coffee:{target:16,plans:[
      {categories:["catering.cafe.coffee_shop","catering.cafe.coffee"],m:1,min:3500},
      {categories:["catering.cafe"],m:1.45,min:6000}
    ]},
    food:{target:24,plans:[
      {categories:["catering.restaurant"],m:1,min:6000},
      {categories:["catering.cafe","catering.fast_food","catering.food_court"],m:1.15,min:7500},
      {categories:["catering"],m:1.45,min:10000}
    ]},
    dessert:{target:16,plans:[
      {categories:["catering.cafe.ice_cream"],m:1,min:5000},
      {categories:["catering.cafe"],m:1.35,min:7500},
      {categories:["catering"],m:1.6,min:10000}
    ]},
    live_music:{target:18,plans:[
      {categories:["entertainment.music_venue"],m:1.2,min:10000},
      {categories:["catering.bar","catering.pub"],m:1.55,min:16000},
      {categories:["entertainment"],m:1.8,min:22000}
    ]},
    nightlife:{target:20,plans:[
      {categories:["catering.bar","catering.pub"],m:1.15,min:9000},
      {categories:["entertainment"],m:1.45,min:14000},
      {categories:["catering.restaurant"],m:1.6,min:18000}
    ]},
    shopping:{target:30,plans:[
      {
        categories:[
          "commercial.shopping_mall",
          "commercial.department_store",
          "commercial.discount_store"
        ],
        m:1.05,
        min:10000
      },
      {
        categories:[
          "commercial.clothing",
          "commercial.jewelry",
          "commercial.books",
          "commercial.art",
          "commercial.antiques"
        ],
        m:1.35,
        min:14000
      },
      {
        categories:[
          "commercial.marketplace",
          "commercial.second_hand",
          "commercial.outdoor_and_sport",
          "commercial.toy_and_game",
          "commercial.houseware_and_hardware"
        ],
        m:1.65,
        min:18000
      }
    ]},
    waterfront:{target:28,plans:[
      {categories:["beach","natural.water"],m:1.35,min:12000},
      {categories:["leisure.park","tourism.attraction"],m:1.55,min:16000},
      {categories:["catering.restaurant","catering.bar","catering.pub"],m:1.7,min:20000},
      {categories:["tourism"],m:1.9,min:24000}
    ]},
    outdoors:{target:24,plans:[
      {categories:["leisure.park","natural"],m:1.15,min:9000},
      {categories:["tourism.attraction","camping"],m:1.45,min:15000},
      {categories:["tourism"],m:1.75,min:22000}
    ]},
    hidden_gems:{target:28,plans:[
      {categories:["tourism.attraction","leisure"],m:1.2,min:10000},
      {categories:["catering.restaurant","catering.cafe"],m:1.45,min:15000},
      {categories:["tourism","catering"],m:1.7,min:22000}
    ]},
    local_favorites:{target:30,plans:[
      {categories:["catering.restaurant","catering.cafe"],m:1.1,min:8000},
      {categories:["entertainment","leisure","tourism.attraction"],m:1.35,min:13000},
      {categories:["catering","tourism"],m:1.55,min:18000}
    ]},
    trending:{target:28,plans:[
      {categories:["catering.restaurant","catering.bar","catering.pub"],m:1.15,min:9000},
      {categories:["entertainment","tourism.attraction"],m:1.45,min:15000},
      {categories:["catering","entertainment"],m:1.8,min:22000}
    ]},
    worth_the_drive:{target:30,plans:[
      {categories:["tourism.attraction","entertainment"],m:1.35,min:14000},
      {categories:["catering.restaurant","leisure"],m:1.65,min:22000},
      {categories:["tourism","natural","catering"],m:2.05,min:32000}
    ]},
    family_iq:{target:26,plans:[
      {categories:["entertainment","leisure.park"],m:1.1,min:9000},
      {categories:["tourism.attraction","leisure"],m:1.4,min:14000},
      {categories:["catering.cafe.ice_cream"],m:1.5,min:16000}
    ]},
    gameday:{target:20,plans:[
      {categories:["catering.bar","catering.pub"],m:1.1,min:9000},
      {categories:["entertainment","catering.restaurant"],m:1.4,min:14000}
    ]},
    dog_iq:{target:18,plans:[
      {categories:["leisure.park","natural"],m:1.1,min:9000},
      {categories:["catering.restaurant"],m:1.35,min:13000}
    ]},
    lake_iq:{target:24,plans:[
      {categories:["natural.water","beach"],m:1.35,min:14000},
      {categories:["camping","leisure.park","tourism.attraction"],m:1.65,min:20000},
      {categories:["tourism"],m:1.9,min:26000}
    ]},
    sunset_iq:{target:20,plans:[
      {categories:["beach","natural","leisure.park"],m:1.3,min:12000},
      {categories:["tourism.attraction"],m:1.55,min:18000}
    ]}
  };
  return profiles[key]||{target:24,plans:[
    {categories:categories(key),m:1,min:8000},
    {categories:categories(key),m:1.55,min:14000}
  ]};
}

function marketAwareCategoryTarget(category,profileTarget,context){
  var key=canonicalCategory(category);
  var marketProfile=context&&context.marketVolumeProfile&&
    typeof context.marketVolumeProfile==="object"
      ? context.marketVolumeProfile
      : {};
  var marketClass=categoryKey(
    marketProfile.marketClass||"standard"
  );
  var target=Math.max(
    0,
    Number(profileTarget||0),
    Number(context&&context.targetResults||0),
    Number(marketProfile.target||0),
    Number(marketProfile.minimum||0)
  );

  if(marketClass==="major"||marketClass==="major_metro"||marketClass==="major-metro"){
    if(key==="food"){target=Math.max(target,42);}
    if(key==="live_music"){target=Math.max(target,32);}
    if(key==="nightlife"){target=Math.max(target,34);}
    if(key==="shopping"){target=Math.max(target,38);}
    if(key==="local_favorites"){target=Math.max(target,42);}
  }else if(
    marketClass==="large_city"||
    marketClass==="large-city"
  ){
    if(key==="food"){target=Math.max(target,50);}
    if(key==="coffee"){target=Math.max(target,28);}
    if(key==="live_music"){target=Math.max(target,30);}
    if(key==="nightlife"){target=Math.max(target,30);}
    if(key==="shopping"){target=Math.max(target,36);}
    if(key==="local_favorites"){target=Math.max(target,40);}
  }else if(
    marketClass==="tourist"||
    marketClass==="tourist_destination"||
    marketClass==="tourist-destination"||
    marketClass==="destination"
  ){
    if(key==="food"){target=Math.max(target,34);}
    if(key==="live_music"){target=Math.max(target,26);}
    if(key==="nightlife"){target=Math.max(target,28);}
    if(key==="shopping"){target=Math.max(target,32);}
    if(key==="local_favorites"){target=Math.max(target,36);}
  }else if(
    marketClass==="small"||
    marketClass==="small_town"||
    marketClass==="very_small"||
    marketClass==="very_small_town"||
    marketClass==="sparse"||
    marketClass==="rural"
  ){
    if(key==="food"){target=Math.max(target,18);}
    if(key==="live_music"){target=Math.max(target,12);}
    if(key==="shopping"){target=Math.max(target,18);}
  }

  return target;
}

function passPlan(context){
  var category=canonicalCategory(
    context.requestedCategory||
    context.category
  );
  var round=discoveryRound(context);
  var radiusMultiplier=1+((round-1)*0.55);
  var baseRadius=Math.max(
    1000,
    Number(context.radiusMeters||cfg.scan.defaultRadiusMeters||8000)
  )*radiusMultiplier;
  var maxRadius=Math.max(baseRadius,Number(
    context.maximumRadiusMeters||
    cfg.scan.maximumRadiusMeters||
    cfg.scan.widerRadiusMeters||
    30000
  ));
  var profile=categoryProfile(category);
  var marketProfile=context.marketVolumeProfile&&
    typeof context.marketVolumeProfile==="object"
      ? context.marketVolumeProfile
      : {};
  var requestedTarget=Math.max(
    0,
    Number(context.targetResults||0),
    Number(marketProfile.target||0),
    Number(marketProfile.minimum||0)
  );
  var effectiveTarget=marketAwareCategoryTarget(
    category,
    profile.target,
    context
  );

  return {
    category:category,
    target:effectiveTarget,
    categoryTarget:Number(profile.target||0),
    requestedTarget:requestedTarget,
    marketClass:String(marketProfile.marketClass||"standard"),
    discoveryRound:round,
    plans:profile.plans.map(function(plan,index){
      return {
        pass:index+1,
        categories:plan.categories.slice(),
        radiusMeters:Math.min(maxRadius,Math.max(Number(plan.min||baseRadius),Math.round(baseRadius*Number(plan.m||1))))
      };
    }).slice(round-1).concat(
      profile.plans.slice(0,Math.max(0,round-1)).map(function(plan,index){
        return {
          pass:profile.plans.length+index+1,
          categories:plan.categories.slice(),
          radiusMeters:Math.min(
            maxRadius,
            Math.max(
              Number(plan.min||baseRadius),
              Math.round(baseRadius*Number(plan.m||1))
            )
          )
        };
      })
    )
  };
}

function runPlans(options){
  var merged=[];
  var completed=[];
  var index=0;

  function next(){
    var plan;
    if(
      index>=options.plan.plans.length||
      (
        options.forceAllPasses!==true&&
        merged.length>=options.plan.target
      )
    ){
      return Promise.resolve(merged);
    }
    plan=options.plan.plans[index];
    index+=1;
    return fetchPass({
      requestId:options.requestId,
      pass:plan.pass,
      category:options.plan.category,
      categories:plan.categories,
      radiusMeters:plan.radiusMeters,
      center:options.center,
      limit:options.limit,
      query:options.query,
      effectiveTarget:options.plan.target,
      bypassCache:options.bypassCache===true||plan.pass>1,
      offset:options.offset||0,
      discoveryRound:options.discoveryRound||1,
      biasProximity:options.biasProximity!==false,
      sessionKey:options.sessionKey||"",
      timeoutMs:options.timeoutMs||GEOAPIFY_PASS_TIMEOUT_MS
    }).then(function(group){
      completed.push({
        pass:plan.pass,categories:plan.categories.slice(),
        radiusMeters:plan.radiusMeters,resultCount:group.length,failed:false
      });
      merged=mergeCandidates([merged,group]);
      return next();
    }).catch(function(error){
      completed.push({
        pass:plan.pass,categories:plan.categories.slice(),
        radiusMeters:plan.radiusMeters,resultCount:0,failed:true,
        message:String(error&&error.message?error.message:error||"Provider pass failed")
      });
      emit("candidate-source:pass-failed",completed[completed.length-1]);
      return next();
    });
  }

  return next().then(function(output){
    return {
      items:output,
      passes:completed,
      target:options.plan.target,
      categoryTarget:options.plan.categoryTarget,
      requestedTarget:options.plan.requestedTarget,
      marketClass:options.plan.marketClass,
      category:options.plan.category
    };
  });
}


function validCenter(value){
  var lat=finiteNumber(value&&value.lat);
  var lng=finiteNumber(value&&value.lng);

  return lat!==null&&lng!==null&&
    Math.abs(lat)<=90&&Math.abs(lng)<=180;
}

function normalizedCenter(value,fallbackType){
  if(!validCenter(value)){return null;}

  return {
    lat:Number(value.lat),
    lng:Number(value.lng),
    type:String(value.type||fallbackType||"center"),
    source:String(value.source||"")
  };
}

function uniqueCenters(values){
  var output=[];
  var seen={};

  (values||[]).forEach(function(value,index){
    var center=normalizedCenter(value,index===0?"primary":"recovery-anchor");
    var key;

    if(!center){return;}

    key=center.lat.toFixed(5)+"|"+center.lng.toFixed(5);

    if(seen[key]){return;}

    seen[key]=true;
    output.push(center);
  });

  return output;
}

function destinationContext(supplied,primaryCenter,category){
  var destination=supplied.destination||null;
  var iq=supplied.destinationIQ||null;
  var expected=0;
  var decision=null;
  var anchors=[];

  try{
    if(!destination&&destinationAuthority&&
      typeof destinationAuthority.current==="function"){
      destination=destinationAuthority.current();
    }

    if(!iq&&destinationAuthority&&
      typeof destinationAuthority.intelligence==="function"){
      iq=destinationAuthority.intelligence(destination);
    }

    if(destinationAuthority&&
      typeof destinationAuthority.expectedFor==="function"){
      expected=Number(
        destinationAuthority.expectedFor(category,destination)||0
      );
    }
  }catch(error){}

  anchors.push(primaryCenter);

  if(iq&&Array.isArray(iq.searchAnchors)){
    anchors=anchors.concat(iq.searchAnchors);
  }

  if(Array.isArray(supplied.searchAnchors)){
    anchors=anchors.concat(supplied.searchAnchors);
  }

  anchors=uniqueCenters(anchors);

  return {
    destination:destination,
    iq:iq,
    expected:expected,
    anchors:anchors,
    strategy:iq&&iq.scanStrategy?iq.scanStrategy:{},
    recovery:iq&&iq.recovery?iq.recovery:{}
  };
}

function destinationRecoveryDecision(category,foundCount,destinationInfo){
  var decision=null;

  try{
    if(destinationAuthority&&
      typeof destinationAuthority.recoveryDecision==="function"){
      decision=destinationAuthority.recoveryDecision(
        category,
        foundCount,
        destinationInfo.destination
      );
    }
  }catch(error){}

  if(!decision){
    var expected=Number(destinationInfo.expected||0);
    var found=Math.max(0,Number(foundCount||0));
    var ratio=expected>0?found/expected:1;

    decision={
      expected:expected,
      found:found,
      ratio:ratio,
      recover:expected>0&&ratio<0.55,
      severe:expected>0&&ratio<0.20,
      strategy:destinationInfo.strategy,
      anchors:destinationInfo.anchors
    };
  }

  return decision;
}

function enrichedMarketProfile(supplied,destinationInfo,category){
  var source=supplied.marketVolumeProfile&&
    typeof supplied.marketVolumeProfile==="object"
      ? supplied.marketVolumeProfile
      : {};
  var output={};
  var key;

  for(key in source){
    if(Object.prototype.hasOwnProperty.call(source,key)){
      output[key]=source[key];
    }
  }

  if(destinationInfo.iq){
    output.marketClass=
      output.marketClass||
      destinationInfo.iq.marketClass||
      "standard";
  }

  output.target=Math.max(
    Number(output.target||0),
    Number(output.minimum||0),
    Number(destinationInfo.expected||0),
    Number(supplied.targetResults||0)
  );

  output.minimum=Math.max(
    Number(output.minimum||0),
    Math.ceil(Number(destinationInfo.expected||0)*0.55)
  );

  if(!output.providerLimit){
    output.providerLimit=Math.min(
      100,
      Math.max(36,Math.ceil(output.target*1.45))
    );
  }

  return output;
}

function expandedRadius(value,multiplier,maximum){
  var base=Math.max(1000,Number(value||0));
  var max=Math.max(base,Number(maximum||30000));

  return Math.min(
    max,
    Math.round(base*Math.max(1,Number(multiplier||1)))
  );
}

function search(context){
  var supplied=context||{};
  var key=String(
    cfg.api&&cfg.api.geoapifyKey||""
  );
  var suppliedCenter=supplied.center||supplied.location||{};
  var primaryCenter=normalizedCenter(suppliedCenter,"primary");
  var requestedCategory=String(
    supplied.requestedCategory||
    supplied.category||
    ""
  );
  var category=canonicalCategory(requestedCategory);
  var round=discoveryRound(supplied);
  var query=clean(
    supplied.canonicalQuery||
    supplied.query||
    requestedCategory||
    supplied.category||
    ""
  );
  var requestId=String(
    supplied.requestId||
    supplied.token||
    Date.now()
  );
  var destinationInfo;
  var marketProfile;
  var requestedLimit;
  var limit;
  var sessionKey;
  var offset;
  var baseRadius;
  var maximumRadius;
  var allItems=[];
  var allPasses=[];
  var attemptedCenters=[];
  var recoveryRounds=0;
  var recoveryDecision=null;

  if(!key||!primaryCenter){
    emit("candidate-source:invalid-request",{
      requestId:requestId,
      hasKey:!!key,
      center:suppliedCenter
    });

    return Promise.resolve([]);
  }

  destinationInfo=destinationContext(
    supplied,
    primaryCenter,
    category
  );

  marketProfile=enrichedMarketProfile(
    supplied,
    destinationInfo,
    category
  );

  requestedLimit=Number(firstDefined(
    supplied.maximumResults,
    supplied.maxResults,
    supplied.limit,
    supplied.pageSize,
    supplied.candidateLimit,
    supplied.resultLimit,
    marketProfile.providerLimit,
    cfg.scan.maximumCandidateResults,
    100
  ));

  limit=Math.max(
    24,
    Math.min(
      100,
      isFinite(requestedLimit)
        ? requestedLimit
        : 100
    )
  );

  sessionKey=requestSessionKey(
    supplied,
    primaryCenter,
    requestedCategory
  );

  offset=Math.max(0,(round-1)*limit);

  baseRadius=Math.max(
    1000,
    Number(
      supplied.radiusMeters||
      cfg.scan.defaultRadiusMeters||
      8000
    )
  );

  maximumRadius=Math.max(
    baseRadius,
    Number(
      supplied.maximumRadiusMeters||
      cfg.scan.maximumRadiusMeters||
      cfg.scan.widerRadiusMeters||
      30000
    )
  );

  requestHistory[sessionKey]={
    round:round,
    offset:offset,
    updatedAt:Date.now()
  };
  trimRequestHistory();

  function runAtCenter(center,options){
    var settings=options||{};
    var plans=passPlan({
      category:supplied.category,
      requestedCategory:requestedCategory,
      discoveryRound:round,
      continuationRound:round,
      radiusMeters:settings.radiusMeters||baseRadius,
      maximumRadiusMeters:maximumRadius,
      targetResults:Math.max(
        Number(supplied.targetResults||0),
        Number(destinationInfo.expected||0)
      ),
      marketVolumeProfile:marketProfile
    });

    attemptedCenters.push({
      lat:center.lat,
      lng:center.lng,
      type:center.type||settings.type||"center",
      radiusMeters:settings.radiusMeters||baseRadius
    });

    return runPlans({
      requestId:requestId+"-"+attemptedCenters.length,
      plan:plans,
      center:{lat:center.lat,lng:center.lng},
      limit:limit,
      query:query,
      bypassCache:
        supplied.bypassCache===true||
        supplied.noCache===true||
        supplied.forceFreshMarket===true||
        round>1||
        settings.forceFresh===true,
      forceAllPasses:
        round>1||
        settings.forceAllPasses===true,
      timeoutMs:GEOAPIFY_PASS_TIMEOUT_MS,
      offset:settings.offset!=null
        ? settings.offset
        : offset,
      discoveryRound:round,
      biasProximity:settings.biasProximity!==false,
      sessionKey:sessionKey+"|"+
        Number(center.lat).toFixed(4)+"|"+
        Number(center.lng).toFixed(4)
    }).then(function(result){
      allItems=mergeCandidates([allItems,result.items]);
      allPasses=allPasses.concat(result.passes.map(function(pass){
        pass.anchorType=center.type||settings.type||"center";
        pass.anchorLat=center.lat;
        pass.anchorLng=center.lng;
        return pass;
      }));
      return result;
    });
  }

  function runRecovery(){
    var anchors=destinationInfo.anchors.slice(1);
    var strategy=destinationInfo.strategy||{};
    var maxRounds=Math.max(
      1,
      Number(strategy.maxRecoveryRounds||3)
    );
    var anchorLimit=strategy.multiAnchor
      ? Math.min(anchors.length,maxRounds)
      : Math.min(anchors.length,1);
    var chain=Promise.resolve();
    var i;

    recoveryDecision=destinationRecoveryDecision(
      category,
      allItems.length,
      destinationInfo
    );

    if(!recoveryDecision.recover){
      return chain;
    }

    emit("candidate-source:recovery-started",{
      requestId:requestId,
      category:category,
      query:query,
      expected:recoveryDecision.expected,
      found:recoveryDecision.found,
      ratio:recoveryDecision.ratio,
      severe:recoveryDecision.severe,
      strategy:strategy,
      anchorCount:anchorLimit
    });

    for(i=0;i<anchorLimit;i+=1){
      (function(anchor,index){
        chain=chain.then(function(){
          var latest=destinationRecoveryDecision(
            category,
            allItems.length,
            destinationInfo
          );

          if(!latest.recover){return null;}

          recoveryRounds+=1;

          return runAtCenter(anchor,{
            radiusMeters:expandedRadius(
              baseRadius,
              1.25+(index*0.25),
              maximumRadius
            ),
            forceFresh:true,
            forceAllPasses:true,
            biasProximity:false,
            offset:0
          });
        });
      })(anchors[i],i);
    }

    chain=chain.then(function(){
      var latest=destinationRecoveryDecision(
        category,
        allItems.length,
        destinationInfo
      );

      if(!latest.recover){return null;}

      recoveryRounds+=1;

      return runAtCenter(primaryCenter,{
        radiusMeters:expandedRadius(
          baseRadius,
          latest.severe?2.4:1.85,
          maximumRadius
        ),
        forceFresh:true,
        forceAllPasses:true,
        biasProximity:false,
        offset:0,
        type:"wider-radius-recovery"
      });
    });

    return chain.then(function(){
      recoveryDecision=destinationRecoveryDecision(
        category,
        allItems.length,
        destinationInfo
      );

      emit("candidate-source:recovery-completed",{
        requestId:requestId,
        category:category,
        query:query,
        expected:recoveryDecision.expected,
        found:allItems.length,
        ratio:recoveryDecision.ratio,
        stillShort:recoveryDecision.recover,
        recoveryRounds:recoveryRounds,
        attemptedCenters:attemptedCenters.slice()
      });
    });
  }

  return runAtCenter(primaryCenter,{
    radiusMeters:baseRadius,
    forceAllPasses:round>1,
    biasProximity:round===1,
    offset:offset
  })
    .then(runRecovery)
    .then(function(){
      recoveryDecision=recoveryDecision||
        destinationRecoveryDecision(
          category,
          allItems.length,
          destinationInfo
        );

      if(allItems.length===8&&limit>8){
        try{
          if(window.console&&window.console.warn){
            window.console.warn(
              "[Drive IQ Adventure V601] [Candidate Source] Returned exactly 8 despite larger destination request",
              {
                requestedLimit:requestedLimit,
                effectiveLimit:limit,
                expectedInventory:destinationInfo.expected,
                marketClass:marketProfile.marketClass,
                category:category,
                query:query
              }
            );
          }
        }catch(warnError){}
      }

      emit("candidate-source:completed",{
        requestId:requestId,
        category:category,
        requestedCategory:requestedCategory,
        query:query,
        discoveryRound:round,
        continuationMode:continuationMode(supplied),
        sessionKey:sessionKey,
        offset:offset,
        safeTimeoutAuthority:true,
        destinationIQConnected:!!destinationInfo.iq,
        destinationType:destinationInfo.iq
          ? destinationInfo.iq.destinationType
          : "",
        marketClass:marketProfile.marketClass||"standard",
        scanStrategy:destinationInfo.strategy,
        expectedInventory:Number(destinationInfo.expected||0),
        count:allItems.length,
        requestedLimit:requestedLimit,
        effectiveLimit:limit,
        candidateShortfall:Math.max(
          0,
          Number(destinationInfo.expected||0)-allItems.length
        ),
        recoveryRequested:!!recoveryDecision.recover,
        recoverySevere:!!recoveryDecision.severe,
        recoveryRatio:recoveryDecision.ratio,
        recoveryRounds:recoveryRounds,
        attemptedCenters:attemptedCenters,
        passes:allPasses,
        forceFreshMarket:supplied.forceFreshMarket===true,
        resetDiscovery:supplied.resetDiscovery===true
      });

      return allItems;
    })
    .catch(function(error){
      emit("candidate-source:failed",{
        requestId:requestId,
        category:supplied.category,
        query:query,
        attemptedCenters:attemptedCenters,
        recoveredCount:allItems.length,
        message:String(
          error&&error.message
            ? error.message
            : error||"Candidate search failed"
        )
      });

      if(allItems.length){
        return allItems;
      }

      throw error;
    });
}

window.DriveIQAdventureCandidateSource={
  search:search,
  searchGeoapify:search,
  providerAuthority:"geoapify",
  categories:categories,
  canonicalCategory:canonicalCategory,
  categoryProfile:categoryProfile,
  passPlan:passPlan,
  discoveryRound:discoveryRound,
  requestSessionKey:requestSessionKey,
  normalizeFeature:normalizeFeature,
  marketAwareCategoryTarget:marketAwareCategoryTarget,
  geoapifyPassTimeoutMs:GEOAPIFY_PASS_TIMEOUT_MS,
  destinationRecoveryDecision:destinationRecoveryDecision,
  destinationContext:destinationContext,
  build:"V601_2_SHOPPING_CANDIDATE_AUTHORITY"
};

})(window);
